(function () {
  'use strict';

  const API_URL = window.__chatbotApiUrl || '';
  const STARTERS = [
    'What grows best in my zone?',
    'I need a privacy hedge',
    'Help me pick a shade tree',
    'How do I care for my new plant?',
  ];

  let conversationId = null;
  let isOpen = false;
  let isStreaming = false;
  let hasStarted = false;

  // DOM references
  let widget, panel, messagesEl, inputEl, sendBtn, startersEl, welcomeEl;

  function init() {
    widget = document.querySelector('.chatbot-widget');
    if (!widget) return;

    panel = widget.querySelector('.chatbot-widget__panel');
    messagesEl = widget.querySelector('.chatbot-widget__messages');
    inputEl = widget.querySelector('.chatbot-widget__input');
    sendBtn = widget.querySelector('.chatbot-widget__send');
    startersEl = widget.querySelector('.chatbot-widget__starters');
    welcomeEl = widget.querySelector('.chatbot-widget__welcome');

    // Toggle open/close
    widget.querySelector('.chatbot-widget__trigger').addEventListener('click', toggle);

    // Send message
    sendBtn.addEventListener('click', () => sendMessage());
    inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    });

    // Starter prompts
    startersEl.querySelectorAll('.chatbot-widget__starter').forEach((btn) => {
      btn.addEventListener('click', () => {
        inputEl.value = btn.textContent.trim();
        sendMessage();
      });
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) toggle();
    });

    // Listen for zone changes
    document.addEventListener('zone:changed', (e) => {
      // Zone data will be picked up from localStorage on next message
    });

    // Mobile close button in header
    const headerClose = widget.querySelector('.chatbot-widget__header-close');
    if (headerClose) {
      headerClose.addEventListener('click', toggle);
    }

  }

  function toggle() {
    isOpen = !isOpen;
    widget.classList.toggle('chatbot-widget--open', isOpen);

    if (isOpen) {
      // Don't auto-focus input on touch devices — let users see starters first
      if (!('ontouchstart' in window)) {
        inputEl.focus();
      } else {
        // Explicitly prevent keyboard on mobile open
        inputEl.blur();
        document.activeElement?.blur();
      }
      panel.setAttribute('aria-hidden', 'false');
    } else {
      inputEl.blur();
      panel.setAttribute('aria-hidden', 'true');
    }
  }

  function getContext() {
    const ctx = {};

    // Get zone from localStorage (set by zone-selector.js)
    const zoneData = localStorage.getItem('rooted_elegance_zone');
    if (zoneData) {
      try {
        const parsed = JSON.parse(zoneData);
        ctx.zone = parsed.zone || parsed;
      } catch {
        ctx.zone = zoneData;
      }
    }

    // Current page
    ctx.currentPage = window.location.pathname;

    // Cart total (read from Shopify cart if available)
    const cartCount = document.querySelector('[data-cart-count]');
    if (cartCount) {
      ctx.cartItemCount = parseInt(cartCount.textContent, 10) || 0;
    }

    return ctx;
  }

  async function sendMessage() {
    const text = inputEl.value.trim();
    if (!text || isStreaming) return;

    // Hide welcome/starters on first message
    if (!hasStarted) {
      hasStarted = true;
      if (welcomeEl) welcomeEl.style.display = 'none';
      if (startersEl) startersEl.style.display = 'none';
    }

    // Add user message
    appendMessage('user', text);
    inputEl.value = '';
    inputEl.style.height = 'auto';

    // Show typing indicator
    const typingEl = showTyping();

    isStreaming = true;
    sendBtn.disabled = true;

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          conversationId,
          context: getContext(),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Something went wrong');
      }

      // Remove typing indicator
      typingEl.remove();

      // Create assistant message bubble
      const msgEl = appendMessage('assistant', '');

      // Read SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const event = JSON.parse(data);
            handleStreamEvent(event, msgEl);
          } catch {
            // Skip malformed JSON
          }
        }
      }

      scrollToBottom();
    } catch (err) {
      typingEl.remove();
      appendMessage('assistant', err.message || 'Sorry, something went wrong. Please try again.');
    } finally {
      isStreaming = false;
      sendBtn.disabled = false;
      inputEl.focus();
    }
  }

  function handleStreamEvent(event, msgEl) {
    switch (event.type) {
      case 'conversation_id':
        conversationId = event.id;
        break;

      case 'text':
        appendTextToMessage(msgEl, event.content);
        scrollToBottom();
        break;

      case 'products':
        appendProducts(event.products);
        scrollToBottom();
        break;

      case 'action':
        if (event.action === 'add_to_cart') {
          addToCart(event.variantId, event.quantity || 1);
        }
        break;

      case 'error':
        appendTextToMessage(msgEl, event.message);
        break;

      case 'done':
        // Render markdown formatting in the final message
        renderMarkdown(msgEl);
        break;
    }
  }

  function appendMessage(role, text) {
    const el = document.createElement('div');
    el.className = `chatbot-widget__msg chatbot-widget__msg--${role}`;
    el.textContent = text;
    if (role === 'assistant') {
      el.setAttribute('aria-live', 'polite');
    }
    messagesEl.appendChild(el);
    scrollToBottom();
    return el;
  }

  function appendTextToMessage(el, text) {
    el.textContent += text;
  }

  function renderMarkdown(el) {
    let text = el.textContent;

    // Escape HTML entities first
    text = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Bold: **text**
    text = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

    // Italic: *text* (but not inside bold)
    text = text.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');

    // Links: [text](url)
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

    // Unordered list items: - text (at start of line)
    text = text.replace(/^- (.+)$/gm, '<li>$1</li>');
    text = text.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>');

    // Numbered list items: 1. text
    text = text.replace(/^\d+\.\s+(.+)$/gm, '<li>$1</li>');
    text = text.replace(/((?:<li>.*<\/li>\n?)+)/g, function (match) {
      // Only wrap in <ol> if not already wrapped in <ul>
      if (match.includes('<ul>')) return match;
      return '<ol>' + match + '</ol>';
    });

    // Line breaks: \n\n = paragraph break, \n = line break
    text = text.replace(/\n\n/g, '</p><p>');
    text = text.replace(/\n/g, '<br>');
    text = '<p>' + text + '</p>';

    // Clean up empty paragraphs
    text = text.replace(/<p>\s*<\/p>/g, '');

    if (text !== el.textContent) {
      el.innerHTML = text;
    }
  }

  function appendProducts(products) {
    const container = document.createElement('div');
    container.className = 'chatbot-widget__products';

    for (const p of products) {
      const card = document.createElement('a');
      card.className = 'chatbot-widget__product-card';
      card.href = p.url;

      let imgHtml = '';
      if (p.image && p.image.url) {
        imgHtml = `<img class="chatbot-widget__product-img" src="${escapeHtml(p.image.url)}&width=300" alt="${escapeHtml(p.image.alt || p.title)}" loading="lazy">`;
      }

      card.innerHTML = `
        ${imgHtml}
        <div class="chatbot-widget__product-info">
          <div class="chatbot-widget__product-title">${escapeHtml(p.title)}</div>
          ${p.zones ? `<div class="chatbot-widget__product-zone">Zones ${p.zones.join(', ')}</div>` : ''}
          <div class="chatbot-widget__product-price">${escapeHtml(p.price || p.priceRange || '')}</div>
        </div>
      `;

      container.appendChild(card);
    }

    messagesEl.appendChild(container);
  }

  function showTyping() {
    const el = document.createElement('div');
    el.className = 'chatbot-widget__typing';
    el.innerHTML = `
      <div class="chatbot-widget__typing-dot"></div>
      <div class="chatbot-widget__typing-dot"></div>
      <div class="chatbot-widget__typing-dot"></div>
    `;
    el.setAttribute('aria-label', 'Typing...');
    messagesEl.appendChild(el);
    scrollToBottom();
    return el;
  }

  function scrollToBottom() {
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  async function addToCart(variantId, quantity) {
    try {
      // Extract numeric ID from Shopify GID if needed
      const id = variantId.includes('/') ? variantId.split('/').pop() : variantId;

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity }] }),
      });

      if (response.ok) {
        const data = await response.json();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      }
    } catch (err) {
      console.error('Add to cart failed:', err);
    }
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
